import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Users, Target, Heart, TrendingUp, Shield, Award } from 'lucide-react';

const LOGO_URL = "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/qtk9sn35_MEDX-AI-Welcome-Poster.pptx%20%281%29.png";

const VALUES = [
  { icon: Heart, title: "Empowerment", desc: "We believe every worker deserves access to premium financial tools and rewards, regardless of income level." },
  { icon: Shield, title: "Trust & Security", desc: "Your financial data is protected with bank-grade security. We operate with full regulatory compliance." },
  { icon: TrendingUp, title: "Growth", desc: "We help our members grow their wealth through smart rewards, cashback programmes, and financial education." },
  { icon: Users, title: "Community", desc: "208 000+ members strong. We build a community where workers uplift each other through collective buying power." },
];

const MILESTONES = [
  { year: "2018", title: "Founded", desc: "AfriPerx was born from a vision to reward Africa's workforce." },
  { year: "2020", title: "10,000 Members", desc: "Crossed our first major milestone with 10,000 active members." },
  { year: "2022", title: "Digital Banking Launch", desc: "Introduced the Diamond Card and full digital banking services." },
  { year: "2024", title: "208,000+ Members", desc: "Grew to serve over 208,000 members across South Africa." },
  { year: "2026", title: "Pan-African Expansion", desc: "Expanding our rewards network across the African continent." },
];

export const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white" data-testid="about-page">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-lg border-b border-gray-100 z-50">
        <div className="max-w-6xl mx-auto px-4 py-2 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <img src={LOGO_URL} alt="AfriPerx" className="h-10 w-auto" />
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-sm text-gray-600 hover:text-afriperx-green font-medium transition-colors">Home</Link>
            <Link to="/about" className="text-sm text-afriperx-green font-semibold">About Us</Link>
            <Link to="/products" className="text-sm text-gray-600 hover:text-afriperx-green font-medium transition-colors">Our Products</Link>
            <Link to="/contact" className="text-sm text-gray-600 hover:text-afriperx-green font-medium transition-colors">Contact Us</Link>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/login')} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 font-medium transition-colors">Sign In</button>
            <button onClick={() => navigate('/get-started')} className="px-5 py-2 text-sm bg-afriperx-green hover:bg-afriperx-green-dark text-white rounded-full font-medium transition-colors">Get Started</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-20 pb-16 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center pt-8">
          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold text-afriperx-green bg-green-100 px-4 py-1.5 rounded-full mb-4">About AfriPerx</span>
          <h1 className="font-syne text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 leading-tight">Rewarding Africa's <span className="text-afriperx-green">Workforce</span></h1>
          <p className="text-gray-500 text-base max-w-2xl mx-auto leading-relaxed">
            AfriPerx is a proudly South African fintech platform empowering the working class with exclusive partner rewards, discount vouchers, and premium financial solutions.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-14 px-4">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-8">
          <div className="bg-afriperx-green rounded-3xl p-8 text-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-afriperx-gold/10 rounded-full blur-3xl" />
            <Target className="w-10 h-10 text-afriperx-gold mb-4" />
            <h2 className="font-syne text-2xl font-bold mb-3">Our Mission</h2>
            <p className="text-white/80 leading-relaxed">
              To put more money back into the pockets of hard-working South Africans through a powerful ecosystem of partner rewards, cashback deals, discount vouchers, and digital financial services that are accessible to all.
            </p>
          </div>
          <div className="bg-gray-50 rounded-3xl p-8 relative overflow-hidden border border-gray-100">
            <div className="absolute top-0 right-0 w-32 h-32 bg-afriperx-green/5 rounded-full blur-3xl" />
            <Award className="w-10 h-10 text-afriperx-gold mb-4" />
            <h2 className="font-syne text-2xl font-bold text-gray-900 mb-3">Our Vision</h2>
            <p className="text-gray-500 leading-relaxed">
              To become Africa's leading rewards and financial wellness platform, where every worker has access to the tools and benefits that help them save, earn, and grow — building a continent where financial empowerment is for everyone, not just the few.
            </p>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-14 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center mb-10">
          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold text-afriperx-gold bg-amber-50 px-4 py-1.5 rounded-full mb-4">Our Values</span>
          <h2 className="font-syne text-2xl md:text-3xl font-bold text-gray-900">What We Stand For</h2>
        </div>
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-6">
          {VALUES.map((v, i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-lg hover:border-afriperx-gold/20 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-afriperx-green/10 flex items-center justify-center mb-4">
                <v.icon className="w-6 h-6 text-afriperx-green" />
              </div>
              <h3 className="font-syne font-bold text-gray-900 text-lg mb-2">{v.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Timeline */}
      <section className="py-14 px-4">
        <div className="max-w-4xl mx-auto text-center mb-10">
          <span className="inline-block text-[10px] uppercase tracking-[0.2em] font-semibold text-afriperx-green bg-green-50 px-4 py-1.5 rounded-full mb-4">Our Journey</span>
          <h2 className="font-syne text-2xl md:text-3xl font-bold text-gray-900">Milestones</h2>
        </div>
        <div className="max-w-3xl mx-auto">
          {MILESTONES.map((m, i) => (
            <div key={i} className="flex gap-6 mb-8 last:mb-0">
              <div className="flex flex-col items-center">
                <div className="w-12 h-12 rounded-full bg-afriperx-gold flex items-center justify-center text-white font-syne font-bold text-xs shrink-0">{m.year}</div>
                {i < MILESTONES.length - 1 && <div className="w-0.5 flex-1 bg-afriperx-gold/20 mt-2" />}
              </div>
              <div className="pb-4">
                <h3 className="font-syne font-bold text-gray-900 mb-1">{m.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-afriperx-green to-afriperx-green-dark rounded-3xl p-8 md:p-12 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-48 h-48 bg-afriperx-gold/10 rounded-full blur-3xl" />
            <div className="relative z-10">
              <h2 className="font-syne text-2xl md:text-3xl font-bold text-white mb-3">Ready to Join the Movement?</h2>
              <p className="text-white/80 text-sm md:text-base max-w-xl mx-auto mb-6">Start earning rewards and accessing premium financial solutions today.</p>
              <button onClick={() => navigate('/get-started')} className="px-8 py-3.5 bg-afriperx-gold hover:bg-afriperx-gold-dark text-white font-syne font-bold rounded-full transition-colors text-sm inline-flex items-center gap-2" data-testid="about-cta-btn">
                Get Started <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-10 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <img src={LOGO_URL} alt="AfriPerx" className="h-8 w-auto brightness-200" />
            <div className="flex gap-6">
              <Link to="/" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Home</Link>
              <Link to="/products" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Products</Link>
              <Link to="/contact" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Contact</Link>
              <Link to="/terms" className="text-gray-400 text-xs hover:text-afriperx-gold transition-colors">Terms</Link>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-6 pt-6 text-center">
            <p className="text-gray-600 text-xs">&copy; 2026 AfriPerx. All rights reserved. FSP Licensed.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};
